import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den "Zeiterfassungsbericht"-Dialog
 * (`TimeRecordingReportComponent`). Wird ueber den Hilfe-Button
 * (`<app-dialog-help-button>`) im Hero-Header manuell gestartet.
 * Architektur 1:1 wie `WORK_SHIFT_REPORT_HELP_TOUR`:
 * `category: 'feature-discovery'` → Guided Tour mit Spotlight + Voice-Over.
 *
 * 7 Schritte: Welcome → Format → Sprache → Berichts-Umfang →
 * Inhalte-Tab (Tab-Switch via `onBeforeShow`) → Filter-Tab (Tab-Switch) →
 * Bericht generieren (Tab-Switch zurueck zu 'format').
 */

type ReportTabId = 'format' | 'content' | 'filter';

/**
 * Wechselt programmatisch den Tab im `TimeRecordingReportComponent` durch
 * synthetischen Klick auf den Tab-Button. Idempotent: prueft
 * `aria-selected`, klickt nur wenn der Ziel-Tab noch nicht aktiv ist.
 */
function switchToReportTab(tabId: ReportTabId): void {
  const tabBtn = document.querySelector<HTMLButtonElement>(`#trr-tab-${tabId}`);
  if (tabBtn && tabBtn.getAttribute('aria-selected') !== 'true') {
    tabBtn.click();
  }
}

export const TIME_RECORDING_REPORT_HELP_TOUR: TourConfig = {
  id: TourFeature.TimeRecordingReportHelp,
  version: 1,
  title: 'TIME_RECORDING_REPORT_HELP_TOUR_TITLE',
  description: 'TIME_RECORDING_REPORT_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Zeiterfassung navigieren, dann den
  // "Report"-Toolbar-Button klicken (Anchor `time-recording-report-button`
  // sitzt am `.fc-reportButton-button`).
  launchContext: {
    route: '/time-recording',
    opens: { openerAnchor: AnchorId.TimeRecordingReport, settleMs: 350 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 90,
  // 'feature-discovery' (nicht 'contextual-help'!) → V2-Renderer mit
  // Spotlight + Voice-Over statt HelpModeService mit (i)-Icons.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'TIME_RECORDING_REPORT_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.TimeRecordingReportDialog,
          title: 'TUTORIAL.TIME_RECORDING_REPORT.WELCOME.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_REPORT.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('format'),
        },
        {
          id: 'format',
          anchor: AnchorId.TimeRecordingReportFormatPanel,
          title: 'TUTORIAL.TIME_RECORDING_REPORT.FORMAT.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_REPORT.FORMAT.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('format'),
        },
        {
          id: 'language',
          anchor: AnchorId.TimeRecordingReportLanguage,
          title: 'TUTORIAL.TIME_RECORDING_REPORT.LANGUAGE.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_REPORT.LANGUAGE.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('format'),
        },
        {
          id: 'scope',
          anchor: AnchorId.TimeRecordingReportScope,
          title: 'TUTORIAL.TIME_RECORDING_REPORT.SCOPE.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_REPORT.SCOPE.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'content',
          anchor: AnchorId.TimeRecordingReportContentTabBtn,
          title: 'TUTORIAL.TIME_RECORDING_REPORT.CONTENT.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_REPORT.CONTENT.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('content'),
        },
        {
          id: 'filter',
          anchor: AnchorId.TimeRecordingReportFilterTabBtn,
          title: 'TUTORIAL.TIME_RECORDING_REPORT.FILTER.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_REPORT.FILTER.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('filter'),
        },
        {
          id: 'generate',
          anchor: AnchorId.TimeRecordingReportGenerateBtn,
          title: 'TUTORIAL.TIME_RECORDING_REPORT.GENERATE.TITLE',
          content: 'TUTORIAL.TIME_RECORDING_REPORT.GENERATE.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: () => switchToReportTab('format'),
          showPrevButton: false,
        },
      ],
    },
  ],
};
